import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Plus, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { EmptyState, ExportMenu, ListPageShell, PageHeader, Table } from '~/components/data';
import { ItemCreateDialog } from '~/components/domain';
import { BulkImportWizard } from '~/components/imports';
import { ItemGroupTree } from '~/components/items';
import { Button, Input } from '~/components/primitives';
import { ProvenanceChip } from '~/components/status';
import { fetchItems } from '~/lib/api/inventory';
import { useListPageState } from '~/lib/hooks/useListPageState';
import { useActiveEntityStore } from '~/store/active-entity';
import shared from './_auth._shared.module.css';
import styles from './_auth.items.index.module.css';

export const Route = createFileRoute('/_auth/items/')({
  staticData: { minMainWidth: 960 },
  component: ItemsList,
});

function ItemsList(): JSX.Element {
  // FE-25: URL search-params sync (q + group) для shareable deep-links.
  const initialParams = new URLSearchParams(window.location.search);
  const [q, setQ] = useState(initialParams.get('q') ?? '');
  const [groupId, setGroupId] = useState<string | null>(initialParams.get('group'));
  const [importOpen, setImportOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // FE-24: bulk selection state. Sticky action bar appears при ≥1 selected.
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  // FE-25: URL search-params sync на изменение q/groupId.
  useEffect(() => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (groupId) params.set('group', groupId);
    const next = params.toString();
    const url = `${window.location.pathname}${next ? `?${next}` : ''}`;
    window.history.replaceState({}, '', url);
  }, [q, groupId]);

  // FE-29: keyboard shortcut «N» = open create dialog. Skip if input focused.
  // FE-25 honest-audit: Esc clears active filters (q + group).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput =
        target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable;
      if (e.key === 'Escape' && (q || groupId)) {
        if (
          isInput &&
          target?.tagName === 'INPUT' &&
          (target as HTMLInputElement).type === 'search'
        ) {
          // Allow Esc inside search input — clears q first.
          e.preventDefault();
          setQ('');
          return;
        }
        if (!isInput) {
          e.preventDefault();
          setQ('');
          setGroupId(null);
          return;
        }
      }
      if (e.key !== 'n' && e.key !== 'N' && e.key !== 'т' && e.key !== 'Т') return;
      if (isInput) return;
      e.preventDefault();
      setCreateOpen(true);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [q, groupId]);
  const query = useQuery({
    queryKey: ['items', q, groupId],
    queryFn: () =>
      fetchItems({ ...(q ? { q } : {}), ...(groupId ? { group: groupId } : {}) } as never),
  });
  const state = useListPageState(query);

  const rows = query.data?.items ?? [];
  // F-sync — колонка «Компания» только в агрегатном охвате (холдинг/«Все компании»).
  const aggregateScope = useActiveEntityStore((s) => s.activeScope) !== 'entity';
  const entities = query.data?.entities ?? {};
  const isEmptyTenant = !query.isLoading && rows.length === 0 && !q && !groupId;

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.nav_items()}
        description={m.route_items_index_description()}
        actions={
          <>
            <ExportMenu resource="items" filters={{ q }} />
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Upload size={14} />}
              onClick={() => setImportOpen(true)}
            >
              {m.route_items_index_btn_bulk_import()}
            </Button>
            <Button
              leadingIcon={<Plus size={14} />}
              onClick={() => setCreateOpen(true)}
              title="Клавиша N — открыть диалог создания"
            >
              {m.route_items_index_btn_new()}
              <span
                style={{
                  marginLeft: 6,
                  padding: '0 4px',
                  fontSize: 10,
                  background: 'rgba(0,0,0,0.15)',
                  borderRadius: 3,
                  fontFamily: 'var(--font-mono)',
                }}
                aria-hidden="true"
              >
                N
              </span>
            </Button>
          </>
        }
      />

      <div className={styles.layout}>
        <aside className={styles.sidebar}>
          <ItemGroupTree selectedId={groupId} onSelect={setGroupId} />
        </aside>
        <div>
          {/* FE-24: 3 Hero metrics — total / source breakdown / variants count. */}
          {rows.length > 0 && (
            <div className={shared.heroRow} style={{ marginBottom: 'var(--space-3)' }}>
              <div className={shared.elevatedCard}>
                <div className={shared.statLabel}>{m.ui_vsego_poziciy()}</div>
                <strong style={{ fontSize: 24, fontFamily: 'var(--font-mono)' }}>
                  {rows.length}
                </strong>
              </div>
              <div className={shared.elevatedCard}>
                <div className={shared.statLabel}>{m.ui_iz_1s()}</div>
                <strong style={{ fontSize: 24, fontFamily: 'var(--font-mono)' }}>
                  {rows.filter((r) => r.source_system === '1c').length}
                </strong>
              </div>
              <div className={shared.elevatedCard}>
                <div className={shared.statLabel}>STAITER-native</div>
                <strong style={{ fontSize: 24, fontFamily: 'var(--font-mono)' }}>
                  {rows.filter((r) => r.source_system === 'staiter').length}
                </strong>
              </div>
            </div>
          )}
          <section className={shared.sectionCard}>
            <Input
              label={m.route_items_index_search_label()}
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={m.route_items_index_search_placeholder()}
            />
            {/* FE-25 honest-audit: active filter chips visible — q + group, Esc clears. */}
            {(q || groupId) && (
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  marginTop: 8,
                }}
                role="region"
                aria-label="Активные фильтры"
              >
                {q && (
                  <span
                    style={{
                      padding: '2px 8px',
                      fontSize: 12,
                      background: 'var(--accent-bg-15)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 12,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    Поиск: «{q}»
                    <button
                      type="button"
                      aria-label="Сбросить поиск"
                      onClick={() => setQ('')}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--text-muted)',
                        padding: '0 2px',
                      }}
                    >
                      ×
                    </button>
                  </span>
                )}
                {groupId && (
                  <span
                    style={{
                      padding: '2px 8px',
                      fontSize: 12,
                      background: 'var(--accent-bg-15)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 12,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    Группа: {groupId.slice(0, 8)}
                    <button
                      type="button"
                      aria-label="Сбросить группу"
                      onClick={() => setGroupId(null)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--text-muted)',
                        padding: '0 2px',
                      }}
                    >
                      ×
                    </button>
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setQ('');
                    setGroupId(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    fontSize: 12,
                    textDecoration: 'underline',
                  }}
                >
                  {m.ui_sbrosit_vse_esc()}
                </button>
              </div>
            )}
          </section>
          {isEmptyTenant ? (
            <EmptyState
              title={m.route_items_index_empty_tenant_title()}
              description={m.route_items_index_empty_tenant_description()}
              action={
                <div className={styles.emptyActions}>
                  <Button
                    variant="primary"
                    leadingIcon={<Plus size={14} />}
                    onClick={() => setCreateOpen(true)}
                  >
                    {m.route_items_index_empty_tenant_create()}
                  </Button>
                  <Button
                    variant="secondary"
                    leadingIcon={<Upload size={14} />}
                    onClick={() => setImportOpen(true)}
                  >
                    {m.route_items_index_btn_bulk_import()}
                  </Button>
                </div>
              }
            />
          ) : (
            <ListPageShell
              state={state}
              emptyTitle={m.route_items_index_empty_filter_title()}
              emptyDescription={m.route_items_index_empty_filter_description()}
            >
              {(items) => (
                <Table
                  ariaLabel={m.nav_items()}
                  data={items}
                  rowKey={(i) => i.id}
                  selectable
                  selectedIds={selectedItems}
                  onSelectionChange={setSelectedItems}
                  columns={[
                    {
                      id: 'sku',
                      header: 'SKU',
                      // FE-27 honest-audit: max-width + ellipsis для длинных SKU + tooltip с full.
                      width: '180px',
                      accessor: (i) => (
                        <Link to="/items/$itemId" params={{ itemId: i.id }} className={shared.link}>
                          <span
                            className={shared.mono}
                            title={i.sku}
                            style={{
                              display: 'inline-block',
                              maxWidth: 160,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              verticalAlign: 'middle',
                            }}
                          >
                            {i.sku}
                          </span>
                        </Link>
                      ),
                      mono: true,
                    },
                    ...(aggregateScope
                      ? [
                          {
                            id: 'entity',
                            header: m.project_col_entity(),
                            accessor: (i: (typeof items)[number]) =>
                              entities[i.entity_id]?.name ?? '—',
                            width: '180px',
                          },
                        ]
                      : []),
                    {
                      id: 'name',
                      header: m.route_items_index_col_name(),
                      accessor: (i) => i.name,
                    },
                    {
                      id: 'type',
                      header: m.route_items_index_col_type(),
                      // FE-71 Sprint N+ batch 3: i18n raw enum codes
                      // (raw_material/consumable/finished_good/wip/service).
                      accessor: (i) => m.item_type_label({ type: i.item_type }),
                    },
                    {
                      id: 'uom',
                      header: m.route_items_index_col_uom(),
                      accessor: (i) => m.units_short({ code: i.default_uom }),
                      mono: true,
                    },
                    {
                      id: 'src',
                      header: m.route_items_index_col_source(),
                      // FE-23: ProvenanceChip вместо plain text для visual hierarchy
                      // (source + sync timestamp + ref id).
                      accessor: (i) => (
                        <ProvenanceChip
                          source={i.source_system}
                          {...(i.updated_at ? { fetched_at: i.updated_at } : {})}
                        />
                      ),
                    },
                  ]}
                />
              )}
            </ListPageShell>
          )}
        </div>
      </div>

      <BulkImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        defaultResource="items"
      />
      <ItemCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      {/* FE-24: bulk action bar — sticky bottom при ≥1 selected. */}
      {selectedItems.size > 0 && (
        <div
          style={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: '10px 16px',
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
            zIndex: 100,
          }}
          role="toolbar"
          aria-label="Массовые действия"
        >
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {m.ui_vybrano()} <strong>{selectedItems.size}</strong>
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              alert(`${m.ui_izmenit_gruppu()} (Tier 2 approval) — pending implementation`)
            }
          >
            {m.ui_izmenit_gruppu()}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              alert(`${m.ui_arhivirovat()} (Tier 2 approval) — pending implementation`)
            }
          >
            {m.ui_arhivirovat()}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedItems(new Set())}>
            {m.ui_snyat_vydelenie()}
          </Button>
        </div>
      )}
    </div>
  );
}
